import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

export interface Submission {
  id: string;
  user_id: string;
  studio_id: string;
  name: string;
  email: string;
  project_title: string;
  project_format: string[];
  synopsis: string;
  current_phase: string;
  budget_range: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface SubmissionScore {
  id: string;
  submission_id: string;
  total_score: number;
  genre_score: number;
  budget_score: number;
  audience_score: number;
  distribution_score: number;
  impact_score: number;
  recommendations: string[];
  created_at: string;
}

export function useSubmissions(studioId?: string) {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (studioId) {
      loadSubmissions();
    }
  }, [studioId]);

  const loadSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('filmmaker_submissions')
        .select(`
          *,
          submission_scores (*)
        `)
        .eq('studio_id', studioId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const createSubmission = async (submissionData: Partial<Submission>) => {
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data, error } = await supabase
        .from('filmmaker_submissions')
        .insert([{ ...submissionData, user_id: userData.user.id }])
        .select()
        .single();

      if (error) throw error;
      setSubmissions(prev => [data, ...prev]);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  };

  const updateSubmissionStatus = async (submissionId: string, status: string) => {
    try {
      const { data, error } = await supabase
        .from('filmmaker_submissions')
        .update({ status })
        .eq('id', submissionId)
        .select()
        .single();

      if (error) throw error;
      setSubmissions(prev => 
        prev.map(sub => sub.id === submissionId ? data : sub)
      );
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  };

  const createSubmissionScore = async (
    submissionId: string,
    scoreData: Omit<SubmissionScore, 'id' | 'submission_id' | 'created_at'>
  ) => {
    try {
      const { data, error } = await supabase
        .from('submission_scores')
        .insert([{ ...scoreData, submission_id: submissionId }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  };

  return {
    submissions,
    loading,
    error,
    createSubmission,
    updateSubmissionStatus,
    createSubmissionScore,
    refresh: loadSubmissions,
  };
}